import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaWallet, FaCheckCircle, FaArrowRight, FaKey, FaDownload, FaUpload, FaLock, FaShieldAlt, FaGlobe } from 'react-icons/fa';
import { MdSecurity } from 'react-icons/md';
import ListBuildingModal from '../../components/ListBuildingModal';

export const Block52Wallets = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const walletFeatures = [
    {
      icon: FaGlobe,
      title: 'Browser-Based',
      description: 'Your wallet is generated and stored in your browser for seamless, instant signing when you play poker.',
      color: '#3b82f6'
    },
    {
      icon: FaKey,
      title: 'Cosmos Standard',
      description: 'Block52 follows the Cosmos blockchain standard, making wallets compatible with the wider Cosmos ecosystem.',
      color: '#64ffda'
    },
    {
      icon: FaDownload,
      title: 'Export Your Keys',
      description: 'We encourage you to export your wallet keys and store them securely offline for backup purposes.',
      color: '#3b82f6'
    },
    {
      icon: FaLock,
      title: 'Hot Wallet Design',
      description: 'Browser wallets act as "hot wallets" for gameplay. Withdraw funds to cold storage when not playing.',
      color: '#64ffda'
    }
  ];

  const securityBestPractices = [
    'Export your wallet keys immediately after creation',
    'Store backup keys in a secure, offline location',
    'Never share your private keys with anyone',
    'Use different wallets for large holdings vs. gameplay',
    'Withdraw winnings to cold storage regularly',
    'Only use trusted devices for wallet access'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Block52 Wallets
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Block52 uses Cosmos-standard blockchain wallets that are generated in your browser for seamless
                gameplay. Learn how to create, secure, and manage your wallet for poker on Block52.
              </Text>
            </VStack>

            <Divider />

            {/* Cosmos Standard Section */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Cosmos Standard Wallets
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Block52 is built on the Cosmos SDK, which means it follows the same wallet standards as other
                Cosmos-based blockchains. This makes Block52 wallets compatible with the broader Cosmos ecosystem
                and ensures your funds are secured by battle-tested cryptography.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Your Block52 wallet consists of a public address (like an account number) and a private key (like a password).
                The private key gives you complete control over your funds—anyone with access to your private key can
                access your tokens.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                border="2px solid"
                borderColor="#3b82f6"
              >
                <Flex align="center" mb={2}>
                  <Icon as={MdSecurity} color="#3b82f6" boxSize={6} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Security First
                  </Heading>
                </Flex>
                <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                  Never share your private keys or seed phrase with anyone. Block52 staff will never ask for your keys.
                  Anyone claiming to be from Block52 asking for your keys is a scammer.
                </Text>
              </Box>
            </Box>

            {/* Wallet Features */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Wallet Features
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {walletFeatures.map((feature, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: feature.color,
                      boxShadow: `0 8px 16px ${feature.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${feature.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={feature.icon} color={feature.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {feature.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {feature.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* How It Works */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How Block52 Wallets Work
              </Heading>
              <VStack spacing={4} align="start">
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">1</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Automatic Generation
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        When you first visit the Block52 app, your browser automatically generates a new wallet for you.
                        This happens locally in your browser—no personal information is collected.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">2</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Instant Signing
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        With your wallet stored in the browser, you can sign transactions instantly when playing poker.
                        No manual approvals or external wallet popups—just seamless gameplay.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">3</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Export for Backup
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        You can export your private keys or seed phrase at any time. We strongly encourage you to do this
                        and store your backup securely offline.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">4</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Import When Needed
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        If you clear your browser data or use a different device, you can import your wallet using your
                        backed-up keys. Note: We discourage importing wallets unless necessary for security reasons.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Accessing Your Wallet */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Accessing Your Wallet
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Your wallet can be accessed through the Block52 app. Here's where to find your wallet information:
              </Text>
              <Box
                p={5}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                borderRadius="lg"
                border="1px solid"
                borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
              >
                <Flex align="center" mb={3}>
                  <Icon as={FaWallet} color="#3b82f6" boxSize={6} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Wallet Page
                  </Heading>
                </Flex>
                <Text color={getColor('text.secondary', colorMode)} fontSize="sm" mb={3}>
                  Navigate to the wallet page to view your address, balance, and export options:
                </Text>
                <Button
                  as={Link}
                  href="https://app.block52.xyz/wallet"
                  isExternal
                  size="sm"
                  variant="outline"
                  borderColor="#3b82f6"
                  color="#3b82f6"
                  _hover={{
                    bg: `rgba(59, 130, 246, 0.1)`,
                    textDecoration: 'none',
                  }}
                  rightIcon={<ExternalLinkIcon />}
                >
                  Open Wallet Page
                </Button>
              </Box>
            </Box>

            {/* Hot Wallet vs Cold Storage */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Hot Wallets vs. Cold Storage
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Block52 browser wallets are designed as "hot wallets"—wallets that are online and ready for
                immediate use. While convenient for gameplay, hot wallets are inherently less secure than cold
                storage (offline wallets).
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#3b82f6"
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Hot Wallet (Browser)
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm" mb={2}>
                    Best for: Playing poker, tournament buy-ins, active gameplay
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Keep only what you need for your current session
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.1)' : 'teal.50'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#64ffda"
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Cold Storage (Hardware/Paper)
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm" mb={2}>
                    Best for: Long-term holdings, large amounts, winnings
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Withdraw funds here when not actively playing
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Security Best Practices */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Security Best Practices
              </Heading>
              <List spacing={3} mb={4}>
                {securityBestPractices.map((practice, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="xl" />
                      <Text color={getColor('text.secondary', colorMode)}>{practice}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Future: Passkeys */}
            <Alert
              status="info"
              variant="left-accent"
              borderRadius="lg"
              bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
              borderColor="#3b82f6"
            >
              <AlertIcon color="#3b82f6" />
              <Box>
                <AlertTitle color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Future Enhancement: Passkey Support
                </AlertTitle>
                <AlertDescription color={getColor('text.secondary', colorMode)}>
                  We're exploring passkey-based wallet authentication for even better security and convenience.
                  This would allow you to use biometric authentication (fingerprint, face recognition) instead
                  of managing private keys directly.
                </AlertDescription>
              </Box>
            </Alert>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Block52Wallets;
